import toast from "react-hot-toast";
import { dealFieldConfig } from "../config/dealFieldConfig";

export type DealFieldChange = {
  old: unknown;
  new: unknown;
};

export type DealUpdatePayload = {
  deal_id: number;
  deal_name: string;
  deal_support?: { id: number; name: string };
  deal_lead?: { id: number; name: string };
  changes: Record<string, DealFieldChange>;
};

const EXTRA_FIELD_LABELS: Record<string, string> = {
  updated_by: "Updated by",
};

function getDealFieldLabel(field: string): string {
  if (EXTRA_FIELD_LABELS[field]) {
    return EXTRA_FIELD_LABELS[field];
  }

  for (const section of dealFieldConfig) {
    const match = section.fields.find((config) => config.field === field);
    if (match?.label) {
      return match.label;
    }
  }

  return field
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatChangeValue(value: unknown): string {
  if (value == null || value === "") {
    return "—";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
}

export function isDealUpdatePayload(data: unknown): data is DealUpdatePayload {
  if (!data || typeof data !== "object") {
    return false;
  }

  const payload = data as DealUpdatePayload;
  return (
    typeof payload.deal_id === "number" &&
    typeof payload.deal_name === "string" &&
    payload.changes != null &&
    typeof payload.changes === "object"
  );
}

function formatDealFieldChange(field: string, change: DealFieldChange): string {
  if (field === "updated_by") {
    return formatChangeValue(change.new);
  }

  return `${formatChangeValue(change.old)} → ${formatChangeValue(change.new)}`;
}

function formatDealFieldChangeLine(field: string, change: DealFieldChange): string {
  return `${getDealFieldLabel(field)}: ${formatDealFieldChange(field, change)}`;
}

function sortChangeEntries(
  entries: [string, DealFieldChange][],
): [string, DealFieldChange][] {
  return [...entries].sort(([fieldA], [fieldB]) => {
    if (fieldA === "updated_by") return -1;
    if (fieldB === "updated_by") return 1;
    return getDealFieldLabel(fieldA).localeCompare(getDealFieldLabel(fieldB));
  });
}

const TOAST_COLORS = {
  background: "#E8F5EC",
  accent: "#1B6B45",
  title: "#1B6B45",
  dealName: "#2F4A42",
  detail: "#6B8F82",
  close: "#9AA8A3",
} as const;

function DealUpdateSuccessIcon() {
  return (
    <div
      style={{
        alignItems: "center",
        background: TOAST_COLORS.accent,
        borderRadius: "50%",
        display: "flex",
        flexShrink: 0,
        height: 28,
        justifyContent: "center",
        marginTop: 2,
        width: 28,
      }}
      aria-hidden
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M2.5 7.25L5.75 10.5L11.5 3.75"
          stroke="#fff"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function formatToastChangeLine(field: string, change: DealFieldChange): string {
  if (field === "updated_by") {
    return `${getDealFieldLabel(field)} ${formatChangeValue(change.new)}`;
  }

  return formatDealFieldChangeLine(field, change);
}

export function formatDealUpdateMessage(data: DealUpdatePayload): string {
  const changeEntries = sortChangeEntries(
    Object.entries(data.changes).filter(
      ([, change]) =>
        change != null &&
        typeof change === "object" &&
        ("old" in change || "new" in change),
    ),
  );

  return changeEntries
    .map(([field, change]) => formatDealFieldChangeLine(field, change))
    .join(", ");
}

export function showDealUpdateToast(
  data: unknown,
  onDismiss?: (dealId: number) => void,
): void {
  if (!isDealUpdatePayload(data)) {
    return;
  }

  const changeEntries = sortChangeEntries(
    Object.entries(data.changes).filter(
      ([, change]) =>
        change != null &&
        typeof change === "object" &&
        ("old" in change || "new" in change),
    ),
  );

  if (changeEntries.length === 0) {
    return;
  }

  toast.custom(
    (t) => (
      <div
        style={{
          background: TOAST_COLORS.background,
          border: "1px solid #D4E8DC",
          borderRadius: 10,
          boxShadow: "0 4px 16px rgba(27, 107, 69, 0.12)",
          display: "flex",
          maxWidth: 380,
          opacity: t.visible ? 1 : 0,
          overflow: "hidden",
          transition: "opacity 150ms ease",
        }}
      >
        <div
          style={{
            background: TOAST_COLORS.accent,
            flexShrink: 0,
            width: 6,
          }}
        />
        <div
          style={{
            alignItems: "flex-start",
            display: "flex",
            flex: 1,
            gap: 12,
            minWidth: 0,
            padding: "14px 14px 14px 12px",
            position: "relative",
          }}
        >
          <DealUpdateSuccessIcon />
          <div style={{ flex: 1, minWidth: 0, paddingRight: 16 }}>
            <div
              style={{
                color: TOAST_COLORS.title,
                fontSize: 15,
                fontWeight: 700,
                lineHeight: 1.3,
              }}
            >
              Deal updated
            </div>
            <div
              style={{
                color: TOAST_COLORS.dealName,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.35,
                marginTop: 4,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.deal_name}
            </div>
            <div style={{ display: "grid", gap: 2, marginTop: 6 }}>
              {changeEntries.map(([field, change]) => (
                <div
                  key={field}
                  style={{
                    color: TOAST_COLORS.detail,
                    fontSize: 13,
                    fontWeight: 400,
                    lineHeight: 1.4,
                  }}
                >
                  {formatToastChangeLine(field, change)}
                </div>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onDismiss?.(data.deal_id);
              toast.dismiss(t.id);
            }}
            style={{
              background: "transparent",
              border: "none",
              color: TOAST_COLORS.close,
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: 0,
              position: "absolute",
              right: 12,
              top: 10,
            }}
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      </div>
    ),
    { duration: 20_000 },
  );
}
